import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { CookieService } from './cookie.service';

export interface UserTypes {
  frontDesk: boolean;
  doctor: boolean;
  nurse: boolean;
  biller: boolean;
}

export interface Role {
  roleId: number;
  roleName: string;
}

export interface AuthState {
  userId: number;
  username: string;
  firstName: string;
  lastName: string;
  userTypes: UserTypes;
  roles: Role[];
  isAdministrator: boolean;
  isEmergency: boolean;
  sessionId: number;
  /** Delphi LoginFM.fsHostName / edtDBLocation (e.g. isamednew\BA). */
  dbLocation?: string;
  /** Logical database selected at login (e.g. ISAMED). */
  databaseKey?: string;
  /** From login/profile — controls EMR button, DoseSpot bell, and prescription APIs. */
  canAccessEMR?: boolean;
}

export interface LoginRequest {
  username: string;
  password: string;
  databaseKey: string;
}

export interface LoginResponse {
  status: string;
  message: string;
  data: {
    userId: number;
    username: string;
    firstName: string;
    lastName: string;
    userTypes: UserTypes;
    roles: Role[];
    isAdministrator: boolean;
    isEmergency: boolean;
    sessionId: number;
    token: string;
    refreshToken: string;
    expiresAt: number;
    canAccessEMR?: boolean;
  };
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  expiresAt: number;
  refreshToken: string;
  token: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private authStateSubject = new BehaviorSubject<AuthState | null>(null);
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public readonly currentUser$: Observable<AuthState | null> = this.authStateSubject.asObservable();
  public readonly isAuthenticated$: Observable<boolean> = this.isAuthenticatedSubject.asObservable();

  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private apiService: ApiService,
    private cookieService: CookieService,
    private router: Router
  ) {
    this.initializeAuthState();
  }

  private initializeAuthState(): void {
    const userData = this.cookieService.getUserData();
    const accessToken = this.cookieService.getAccessToken();

    if (userData && accessToken) {
      this.authStateSubject.next(userData);
      this.isAuthenticatedSubject.next(true);
    } else {
      this.authStateSubject.next(null);
      this.isAuthenticatedSubject.next(false);
    }
  }

  login(username: string, password: string, databaseKey: string, dbLocation?: string): Observable<LoginResponse> {
    const loginRequest: LoginRequest = { username, password, databaseKey };

    return this.apiService.post<LoginResponse>('auth/login', loginRequest).pipe(
      tap((response) => {
        if (response.status === 'success' && response.data) {
          const { token, refreshToken, expiresAt, ...userData } = response.data;
          const sessionUser = {
            ...userData,
            databaseKey,
            dbLocation: String(dbLocation ?? '').trim() || undefined
          };

          const expiresIn = expiresAt ? expiresAt - Math.floor(Date.now() / 1000) : undefined;
          this.cookieService.setAccessToken(token, expiresIn);
          this.cookieService.setRefreshToken(refreshToken, expiresIn);
          this.cookieService.setUserData(sessionUser);

          this.authStateSubject.next(sessionUser);
          this.isAuthenticatedSubject.next(true);
        }
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  logout(): Observable<any> {
    // Get sessionId from user data in cookies
    const userData = this.cookieService.getUserData();
    const sessionId = userData?.sessionId;

    // Prepare query parameters
    const params: any = {};
    if (sessionId) {
      params.sessionId = sessionId;
    }

    return this.apiService.post('auth/logout', {}, { params }).pipe(
      tap(() => {
        this.clearAuthState();
        this.router.navigate(['/login']);
      }),
      catchError((error) => {
        this.clearAuthState();
        this.router.navigate(['/login']);
        return throwError(() => error);
      })
    );
  }

  refreshToken(): Observable<RefreshTokenResponse> {
    if (this.isRefreshing) {
      return new Observable<RefreshTokenResponse>((observer) => {
        const subscription = this.refreshTokenSubject.subscribe((token) => {
          if (token) {
            const accessToken = this.cookieService.getAccessToken();
            const refreshToken = this.cookieService.getRefreshToken();
            if (accessToken && refreshToken) {
              observer.next({ 
                token: accessToken, 
                refreshToken: refreshToken, 
                expiresAt: 0 
              });
              observer.complete();
            }
          }
        });
        return () => subscription.unsubscribe();
      });
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    const refreshToken = this.cookieService.getRefreshToken();
    if (!refreshToken) {
      this.isRefreshing = false;
      this.clearAuthState();
      this.router.navigate(['/']);
      return throwError(() => new Error('No refresh token available'));
    }

    const refreshRequest: RefreshTokenRequest = { refreshToken };

    return this.apiService.post<RefreshTokenResponse>('auth/refresh', refreshRequest).pipe(
      tap((response) => {
        const expiresIn = response.expiresAt ? response.expiresAt - Math.floor(Date.now() / 1000) : undefined;
        this.cookieService.setAccessToken(response.token, expiresIn);
        this.cookieService.setRefreshToken(response.refreshToken, expiresIn);

        this.isRefreshing = false;
        this.refreshTokenSubject.next(response.token);
      }),
      catchError((error) => {
        this.isRefreshing = false;
        this.clearAuthState();
        this.router.navigate(['/']);
        return throwError(() => error);
      })
    );
  }

  isAuthenticated(): boolean {
    const hasToken = this.cookieService.hasAccessToken();
    const hasUserData = this.authStateSubject.value !== null;
    
    const isAuth = hasToken && hasUserData;

    if (hasToken && !hasUserData) {
      this.initializeAuthState();
      return this.authStateSubject.value !== null;
    }

    return isAuth;
  }

  getCurrentUser(): AuthState | null {
    return this.authStateSubject.value;
  }

  hasRole(roleName: string): boolean {
    const user = this.getCurrentUser();
    if (!user) {
      return false;
    }
    return user.roles.some((role) => role.roleName === roleName);
  }

  hasAnyRole(roleNames: string[]): boolean {
    return roleNames.some((roleName) => this.hasRole(roleName));
  }

  hasAllRoles(roleNames: string[]): boolean {
    return roleNames.every((roleName) => this.hasRole(roleName));
  }

  hasUserType(userType: keyof UserTypes): boolean {
    const user = this.getCurrentUser();
    if (!user) {
      return false;
    }
    return user.userTypes[userType] === true;
  }

  isAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.isAdministrator === true;
  }

  isEmergency(): boolean {
    const user = this.getCurrentUser();
    return user?.isEmergency === true;
  }

  /** True when login (or stored session) grants EMR / DoseSpot access. */
  canAccessEMR(): boolean {
    const user = this.getCurrentUser();
    if (!user) {
      return false;
    }
    return user.canAccessEMR === true;
  }

  get canAccessEMR$(): Observable<boolean> {
    return new Observable((observer) => {
      const subscription = this.currentUser$.subscribe((user) => {
        observer.next(user?.canAccessEMR === true);
      });
      return () => subscription.unsubscribe();
    });
  }

  get userRoles$(): Observable<Role[]> {
    return new Observable((observer) => {
      const subscription = this.currentUser$.subscribe((user) => {
        observer.next(user?.roles || []);
      });
      return () => subscription.unsubscribe();
    });
  }

  get userTypes$(): Observable<UserTypes | null> {
    return new Observable((observer) => {
      const subscription = this.currentUser$.subscribe((user) => {
        observer.next(user?.userTypes || null);
      });
      return () => subscription.unsubscribe();
    });
  }

  private clearAuthState(): void {
    this.cookieService.clearAll();
    // Clear browser-stored session/auth artifacts so next login starts cleanly.
    try {
      sessionStorage.clear();
    } catch {}
    try {
      localStorage.removeItem('dailyRoster:filters');
      localStorage.removeItem('dailyRoster:tableHeaderFilters');
    } catch {}
    this.authStateSubject.next(null);
    this.isAuthenticatedSubject.next(false);
    this.isRefreshing = false;
    this.refreshTokenSubject.next(null);
  }
}
